import {
  CheckboxController,
  Collapse,
  Text,
} from "@pushpress/shared-components";
import { type Control, Controller, useWatch } from "react-hook-form";
import {
  attendanceLimitOptions,
  classAccessOptionsV2,
  intervalTypeOptions,
  sessionNonRecurringOptions,
  sessionRecurringOptions,
} from "../PlanDrawer.utils";
import { LimitCheckinsType, PlanType } from "../../../../apis/subscriptionApi";
import { PlanForm } from "../PlanDrawer.types";
import ClassesAdvancedOptionsCollapse from "./ClassesAdvancedOptionsCollapse";
import { Select, SelectItem, SelectContent, SelectTrigger, SelectValue, Input } from "@pushpress/pushpress-ui";
import { sortByName } from "../../../../utils/sortUtils";
import { useMemo, useState, useEffect } from "react";
import { useGetCalendarClassTypesQuery } from "../../../../components/controllers/ClassTypeSelect/ClassTypeSelect-generated.hooks";
import { MultipleSelector } from "./multi-select";

interface ClassesSectionProps {
  control: Control<PlanForm>;
  planType: PlanType;
}

export function useClassesSection(props: ClassesSectionProps) {
  const { control, planType } = props;

  const [attendanceLimit, classAccess, openGym, sessionsExpire] = useWatch({
    control,
    name: ["attendanceLimit", "classAccess", "openGym", "sessionsExpire"],
  });

  let classesVisible = false;
  let attendanceLimitVisible = false;
  let sessionsCountVisible = false;
  let sessionsVisible = false;
  let sessionsExpireVisible = false;
  let sessionsExpireInVisible = false;
  let sessionsOptions: {
    label: string;
    value: LimitCheckinsType;
  }[] = [];

  if (planType === "recurring") {
    attendanceLimitVisible = true;
    sessionsOptions = sessionRecurringOptions;
  } else if (planType === "non-recurring") {
    attendanceLimitVisible = true;
    sessionsOptions = sessionNonRecurringOptions;
  } else if (planType === "session-pack") {
    sessionsCountVisible = true;
    sessionsExpireVisible = true;
  }
  if (planType === "recurring" && attendanceLimit.value === "limited") {
    sessionsVisible = true;
  }
  if (planType === "non-recurring" && attendanceLimit.value === "limited") {
    sessionsVisible = true;
  }
  if (classAccess.value === "some") {
    classesVisible = true;
  }
  if (planType === "session-pack" && sessionsExpire) {
    sessionsExpireInVisible = true;
  }
  if (classAccess.value === "none" && !openGym) {
    attendanceLimitVisible = false;
    sessionsVisible = false;
    sessionsCountVisible = false;
    sessionsExpireVisible = false;
    sessionsExpireInVisible = false;
  }

  return {
    classesVisible,
    attendanceLimitVisible,
    sessionsCountVisible,
    sessionsOptions,
    sessionsVisible,
    sessionsExpireVisible,
    sessionsExpireInVisible,
  };
}

const ClassesSection = (props: ClassesSectionProps) => {
  const { control, planType } = props;

  const {
    classesVisible,
    attendanceLimitVisible,
    sessionsCountVisible,
    sessionsVisible,
    sessionsExpireVisible,
    sessionsExpireInVisible,
  } = useClassesSection(props);

  const { data } = useGetCalendarClassTypesQuery({
    variables: {
      active: true,
    },
  });

  const [selected, setSelected] = useState<{label: string; value: string}[]>([])

  const classes = useWatch({
    control,
    name: "classes",
  });

  // Initialize selected state with current form values
  useEffect(() => {
    if (classes && classes.length > 0 && data?.getCalendarClassTypes) {
      const selectedClasses = classes.map((classItem: { uuid: string }) => {
        const classType = data.getCalendarClassTypes.find(
          (ct) => ct.uuid === classItem.uuid
        );
        return {
          label: classType?.name || "",
          value: classItem.uuid,
        };
      });
      setSelected(selectedClasses);
    } else {
      setSelected([]);
    }
  }, [classes, data?.getCalendarClassTypes]);

  const classTypeOptions = useMemo(() => {
    if (!data?.getCalendarClassTypes) {
      return [];
    }
    return [...data.getCalendarClassTypes].sort(
      sortByName,
    ) as unknown as {uuid: string; name: string}[];
  }, [data]);
  
  return (
    <div>
      <Text
        component="h1"
        variant="heading-4"
        weight="600"
        color="wolf-800"
        className="mt-5"
      >
        Plan Access
      </Text>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <div className="mt-3 tw-grid tw-items-center tw-gap-1.5">
          <label htmlFor="classAccess">
            Class access *
          </label>
          <Controller
            control={control}
            name="classAccess"
            render={({ field }) => (
              <Select
                value={field.value?.value || ""}
                onValueChange={(value) => {
                  field.onChange({
                    value,
                  });
                }}  
                required
                name="classAccess"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select class access" />
                </SelectTrigger>
                <SelectContent>
                  {classAccessOptionsV2.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />

          <Collapse in={classesVisible}>
            <div className="mt-3 tw-grid tw-items-center tw-gap-1.5">
              <label htmlFor="classes">
                Classes
              </label>
              <Controller
                control={control}
                name="classes"
                render={({ field }) => (
                  <div className="flex w-full flex-col gap-5 px-10">
                    <MultipleSelector
                      options={classTypeOptions.map((option) => ({
                        label: option.name,
                        value: option.uuid,
                      }))}
                      onChange={(values) => {
                        setSelected(values.map((value) => ({
                          label: classTypeOptions.find((option) => option.uuid === value)?.name || "",
                          value,
                        })));
                        // Update the form field with the selected class UUIDs
                        field.onChange(
                          values.map((value) => ({ uuid: value }))
                        );
                      }}
                      values={selected.map((option) => option.value)}
                      title="classes"
                    />
                  </div>
                )}
              />
            </div>
          </Collapse>
        </div>
        <div>
          <CheckboxController
            control={control}
            name="openGym"
            label="Open gym access"
            className="mt-5 mx-5"
          />
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <div>
          <Collapse in={attendanceLimitVisible}>
            <div className="mt-3 tw-grid tw-items-center tw-gap-1.5">
              <label htmlFor="attendanceLimit">
                Attendance limit
              </label>
              <Controller
                control={control}
                name="attendanceLimit"
                render={({ field }) => (
                  <Select
                    value={field.value?.value || ""}
                    onValueChange={(value) => {
                      field.onChange({
                        value,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select attendance limit" />
                    </SelectTrigger>
                    <SelectContent>
                      {attendanceLimitOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
        </Collapse>
        <Collapse in={sessionsCountVisible}>
          <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
            <label htmlFor="sessionsCount">
              Sessions count *
            </label>
            <Controller
              control={control}
              name="sessionsCount"
              render={({ field }) => (
                <Input
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(parseInt(value));
                  }}
                  placeholder="Sessions count"
                  type="number"
                  min={1}
                  autoComplete="off"
                  required
                  name="sessionsCount"
                />
              )}
            />
          </div>
        </Collapse>
        <Collapse in={sessionsExpireVisible}>
          <CheckboxController
            control={control}
            name="sessionsExpire"
            label="Punchcard expires"
            className="mb-4 tw-mt-3"
          />
        </Collapse>
        <Collapse in={sessionsVisible}>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
              <label htmlFor="sessionsCount">
                Sessions count *
              </label>
              <Controller
                control={control}
                name="sessionsCount"
                render={({ field }) => (
                  <Input
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(parseInt(value));
                    }}
                    placeholder="Sessions count"
                    type="number"
                    min={1}
                    autoComplete="off"
                    required
                    name="sessionsCount"
                  />
                )}
              />
            </div>
            <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
              <label htmlFor="sessionsPeriod">
                Sessions interval *
              </label>
              <Controller
                control={control}
                name="sessionsPeriod"
                render={({ field }) => (
                  <Select
                    value={field.value?.value || ""}
                    onValueChange={(value) => {
                      field.onChange({
                        value,
                      });
                    }}
                    required
                    name="sessionsPeriod"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sessions interval" />
                    </SelectTrigger>
                    <SelectContent>
                      {intervalTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  )}
                />
            </div>
          </div>
        </Collapse>
        <Collapse in={sessionsExpireInVisible}>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
              <label htmlFor="sessionsExpireCount">
                Sessions expire in *
              </label>
              <Controller
                control={control}
                name="sessionsExpireCount"
                render={({ field }) => (
                  <Input
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(parseInt(value));
                    }}
                    placeholder="Sessions expire in"
                    type="number"
                    min={1}
                    autoComplete="off"
                    required
                    name="sessionsExpireCount"
                  />
                )}
              />
            </div>
            <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
              <label htmlFor="sessionsExpireInterval">
                Sessions expire in interval *
              </label>
              <Controller
                control={control}
                name="sessionsExpireInterval"
                render={({ field }) => (
                  <Select
                    value={field.value?.value || ""}
                    onValueChange={(value) => {
                      field.onChange({
                        value,
                      });
                    }}
                    required
                    name="sessionsExpireInterval"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sessions expire in interval" />
                    </SelectTrigger>
                    <SelectContent>
                      {intervalTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </Collapse>
        </div>
      </div>
      <ClassesAdvancedOptionsCollapse control={control} planType={planType} />
    </div>
  );
};

export default ClassesSection;
